import { useMemo } from "react"

import type { FC } from "react"

import type {
  QueryPlotDef,
  DevicePlotDef,
  PowerlinePlotDef,
  TowerPlotDef,
  WeatherServicePlotDef,
  SpanPlotDef,
} from "types/dashboard.types"
import type { Entity } from "types/common.types"

import {
  resolveEntityIds,
  resolverNeedsDevices,
  resolverNeedsPowerlines,
} from "helpers/utils/crossEntityPlots"
import useDevices from "helpers/hooks/useDevices"
import { useEntityGeohashes } from "helpers/hooks/useEntityGeohashes"
import TowerTelemetryPlot from "widgets/plots/towers/TowerTelemetryPlot"
import PowerlinePlot from "widgets/plots/powerline/PowerlinePlot"
import SpanPlot from "widgets/plots/span/SpanPlot"
import WeatherServicePlot from "widgets/plots/weatherService/WeatherServicePlot"
import { useAllPowerlines } from "helpers/hooks/powerlines/useAllPowerlines"
import DevicePlot from "./device/DevicePlot"

interface CrossEntityPlotProps {
  plotDef:
    | QueryPlotDef
    | TowerPlotDef
    | PowerlinePlotDef
    | WeatherServicePlotDef
    | SpanPlotDef
  contextEntity: Entity
}

const CrossEntityPlot: FC<CrossEntityPlotProps> = ({ plotDef, contextEntity }) => {
  const { dataSource } = plotDef

  const isWeatherServicePlot = dataSource.service === "weather"

  const needsDevices = useMemo(
    () => dataSource.resolver !== "self" && resolverNeedsDevices(dataSource.resolver),
    [dataSource],
  )
  const { devsWRelsById } = useDevices({ skip: !needsDevices })

  const needsPowerlines = useMemo(
    () => dataSource.resolver !== "self" && resolverNeedsPowerlines(dataSource.resolver),
    [dataSource],
  )
  const { powerlinesById, powerlineIdByTowerId } = useAllPowerlines({
    skip: !needsPowerlines,
  })

  const entityIds = useMemo(
    () =>
      resolveEntityIds(
        dataSource,
        contextEntity.id,
        contextEntity,
        devsWRelsById,
        needsPowerlines ? powerlinesById : undefined,
        needsPowerlines ? powerlineIdByTowerId : undefined,
      ),
    [
      devsWRelsById,
      needsPowerlines,
      powerlinesById,
      powerlineIdByTowerId,
      contextEntity,
      dataSource,
    ],
  )

  const geohashes = useEntityGeohashes(
    entityIds,
    dataSource.entity,
    !isWeatherServicePlot ||
      (dataSource.entity !== "tower" && dataSource.entity !== "device"),
  )

  if (isWeatherServicePlot) {
    return (
      <WeatherServicePlot
        def={plotDef as WeatherServicePlotDef}
        geohashes={geohashes}
        entityIds={entityIds}
      />
    )
  }

  switch (dataSource.entity) {
    case "device":
      return <DevicePlot def={plotDef as DevicePlotDef} deviceIds={entityIds} />

    case "tower":
      return <TowerTelemetryPlot def={plotDef as TowerPlotDef} towerIds={entityIds} />

    case "powerline":
      return <PowerlinePlot def={plotDef as PowerlinePlotDef} powerlineIds={entityIds} />

    case "span":
      return <SpanPlot def={plotDef as SpanPlotDef} spanIds={entityIds} />

    default:
      return <></>
  }
}

export default CrossEntityPlot
